import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Check, ArrowUpRight, Sparkles, ChevronRight } from "lucide-react";

const PARTNERS = [
  {
    id: "launch",
    name: "Launch Partnerschaft",
    subline: "Für Einzelunternehmer, die online durchstarten wollen — ohne selbst zum Marketing-Experten zu werden.",
    fit: [
      "1–9 Mitarbeitende",
      "Erste Erfahrung mit Online-Verkauf oder gerade gestartet",
      "Kein eigenes Marketing-Know-how im Team",
      "Ziel: erste Online-Kunden gewinnen, ohne selbst Ads/Content zu lernen",
    ],
    setup: "CHF 990 Setup",
    monthly: "ab CHF 590 / Monat",
    single: "CHF 749 / Monat",
    saves: "CHF 159 / Monat",
    features: [
      { name: "Website-Care Starter", value: "CHF 149" },
      { name: "Content-Produktion & UGC-Skripte", value: "CHF 350" },
      { name: "Meta Ads Management Starter", value: "CHF 250" },
      { name: "Erfolgs-Check nach 90 Tagen", value: "CHF 0", bonus: true },
    ],
    adBudget: "zzgl. Werbebudget (individuell, ab ca. CHF 300 / Monat empfohlen)",
    ctaLabel: "Launch-Partnerschaft starten",
  },
  {
    id: "wachstum",
    name: "Wachstums Partnerschaft",
    subline: "Für KMU, die Filiale und Online-Shop strategisch verzahnen und messbar wachsen wollen.",
    highlight: "Beliebteste Wahl",
    fit: [
      "10–49 Mitarbeitende",
      "Mehrere Verkaufskanäle (Filiale + Online), aber unkoordiniert",
      "Erste Marketing-Erfahrung vorhanden, ohne einheitliche Strategie",
      "Ziel: Marke und Vertrieb schweizweit vereinheitlichen und messbar skalieren",
    ],
    setup: "CHF 2'900 Setup",
    monthly: "ab CHF 1'190 / Monat",
    single: "CHF 1'549 / Monat",
    saves: "CHF 359 / Monat",
    features: [
      { name: "Website-Care Wachstum", value: "CHF 249" },
      { name: "Erweiterte Content-Produktion", value: "CHF 550" },
      { name: "Meta Ads Management Growth", value: "CHF 600" },
      { name: "Strategie-Meeting (monatlich)", value: "CHF 150" },
      { name: "Omnichannel Brand-Guidelines", value: "CHF 0", bonus: true },
    ],
    adBudget: "zzgl. Werbebudget (individuell)",
    ctaLabel: "Wachstums-Partnerschaft starten",
  },
  {
    id: "scale",
    name: "Scale Partnerschaft",
    subline: "Für skalierende Marken mit Filialnetz, die Marketing professionalisieren und schweizweit führend werden wollen.",
    fit: [
      "50–249 Mitarbeitende",
      "Filialnetz oder mehrere Standorte, wachsendes Online-Volumen",
      "Marketing / IT / Verkauf arbeiten aktuell in Silos",
      "Ziel: professionelle, datengetriebene Omnichannel-Struktur mit klarer Governance",
    ],
    setup: "CHF 4'900 Setup",
    monthly: "ab CHF 2'190 / Monat",
    single: "CHF 2'540 / Monat",
    saves: "CHF 350 / Monat",
    features: [
      { name: "Website-Care Premium", value: "CHF 390" },
      { name: "Volle Content-Produktion", value: "CHF 900" },
      { name: "Meta Ads Management Scale", value: "CHF 1'000" },
      { name: "Persönlicher Ansprechpartner", value: "CHF 250" },
      { name: "Quartalsweises KPI-Reporting auf Führungsebene", value: "CHF 0", bonus: true },
    ],
    adBudget: "zzgl. Werbebudget (individuell)",
    ctaLabel: "Scale-Partnerschaft anfragen",
  },
];

function PartnerCard({ plan, i }) {
  const ref = useRef(null);
  const [t, setT] = useState({ rx: 0, ry: 0 });
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ rx: -py * 3.5, ry: px * 3.5 });
  };
  const reset = () => setT({ rx: 0, ry: 0 });
  const isHi = !!plan.highlight;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ transform: `perspective(1200px) rotateX(${t.rx}deg) rotateY(${t.ry}deg)` }}
      className={`relative p-8 md:p-10 transition-all duration-500 flex flex-col h-full ${
        isHi
          ? "bg-[#0A0A0A] text-[#FAFAF7] ring-1 ring-[#C9A961]"
          : "bg-white border border-[#E5E5E0]"
      }`}
      data-testid={`partner-card-${plan.id}`}
    >
      {isHi && (
        <div className="absolute -top-3 left-8 bg-[#C9A961] text-[#0A0A0A] px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-medium">
          {plan.highlight}
        </div>
      )}

      {/* Head */}
      <div className="flex items-start justify-between">
        <div>
          <span className={`chapter-num ${isHi ? "text-[#C9A961]" : ""}`}>{String(i + 1).padStart(2, "0")} · Partnerschaft</span>
          <h3 className="font-serif-editorial text-3xl md:text-[38px] leading-[1.05] mt-3">{plan.name}</h3>
        </div>
      </div>
      <p className={`mt-4 leading-relaxed text-[15px] ${isHi ? "text-[#FAFAF7]/70" : "text-[#0A0A0A]/70"}`}>{plan.subline}</p>

      {/* Geeignet für */}
      <div
        className={`mt-6 rounded-none border-l-2 pl-4 py-3 pr-3 ${
          isHi
            ? "border-[#C9A961]/60 bg-white/[0.04]"
            : "border-[#C9A961]/50 bg-[#C9A961]/[0.06]"
        }`}
      >
        <div className={`font-mono-eyebrow mb-2 ${isHi ? "text-[#C9A961]" : "text-[#C9A961]"}`}>
          Geeignet für
        </div>
        <ul className="space-y-1.5">
          {plan.fit.map((f) => (
            <li key={f} className="flex items-start gap-2 text-[13.5px] leading-snug">
              <ChevronRight size={14} className="text-[#C9A961] mt-[3px] flex-shrink-0" strokeWidth={2} />
              <span className={isHi ? "text-[#FAFAF7]/80" : "text-[#0A0A0A]/75"}>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Price block */}
      <div className={`mt-8 pb-8 border-b ${isHi ? "border-white/15" : "border-black/10"}`}>
        <div className={`text-[13px] line-through mb-1 ${isHi ? "text-[#FAFAF7]/40" : "text-[#0A0A0A]/45"}`}>
          Einzeln gebucht: {plan.single}
        </div>
        <div className={`font-serif-editorial text-2xl md:text-[26px] ${isHi ? "text-[#FAFAF7]" : "text-[#0A0A0A]"}`}>{plan.setup}</div>
        <div className={`font-serif-editorial text-2xl md:text-[26px] mt-1 ${isHi ? "text-[#FAFAF7]" : "text-[#0A0A0A]"}`}>+ {plan.monthly}</div>
        <div className="mt-2 font-mono-eyebrow text-[#C9A961]">Festpreis · Alles inklusive</div>
        <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#22C55E]/40 bg-[#22C55E]/[0.08]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
          <span className="text-[11px] font-medium text-[#22C55E] tracking-wide">Sie sparen {plan.saves}</span>
        </div>
        <div className={`mt-3 text-[11px] leading-relaxed ${isHi ? "text-[#FAFAF7]/55" : "text-[#0A0A0A]/50"}`}>
          Mindestlaufzeit 6 Monate · monatlich kündbar danach
        </div>
      </div>

      {/* Features */}
      <ul className="mt-8 space-y-3">
        {plan.features.map((f) => (
          <li key={f.name} className="flex items-start gap-3 text-[15px]">
            <span className="mt-0.5 w-5 h-5 rounded-full border border-[#C9A961] flex items-center justify-center flex-shrink-0">
              <Check size={12} className="text-[#C9A961]" strokeWidth={2.4} />
            </span>
            <span className="flex-1 flex items-baseline justify-between gap-3">
              <span className={isHi ? "text-[#FAFAF7]/90" : "text-[#0A0A0A]/85"}>
                {f.name}
                {f.bonus && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm text-[9.5px] uppercase tracking-[0.12em] font-medium bg-[#C9A961]/15 text-[#C9A961] align-middle">
                    Bonus
                  </span>
                )}
              </span>
              <span className={`text-[12px] tabular-nums whitespace-nowrap ${isHi ? "text-[#FAFAF7]/40" : "text-[#0A0A0A]/40"}`}>{f.value}</span>
            </span>
          </li>
        ))}
      </ul>

      <div className={`mt-5 text-[11px] italic ${isHi ? "text-[#FAFAF7]/50" : "text-[#0A0A0A]/50"}`}>
        {plan.adBudget}
      </div>

      {/* CTA */}
      <div className="mt-auto pt-10">
        <button
          onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
          className={`w-full ${isHi ? "btn-gold" : "btn-ghost"} justify-between`}
          data-testid={`partner-cta-${plan.id}`}
        >
          {plan.ctaLabel} <ArrowUpRight size={16} className="arrow" />
        </button>
        <div className={`mt-3 text-center text-[11px] tracking-wide ${isHi ? "text-[#FAFAF7]/50" : "text-[#0A0A0A]/50"}`}>
          Kostenloses Erstgespräch · Unverbindlich
        </div>
      </div>
    </motion.div>
  );
}

export default function Partnerschaft() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const badgeX = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section
      id="partnerschaft"
      ref={ref}
      className="relative bg-[#FAFAF7] py-24 md:py-36 border-t border-black/5 overflow-hidden"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-[#C9A961]/8 blur-[120px]" />
        <div className="absolute -bottom-32 -right-24 w-[560px] h-[560px] rounded-full bg-[#0A0A0A]/5 blur-[140px]" />
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-10">
        <motion.div
          style={{ x: badgeX }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A961]/50 bg-[#C9A961]/10 mb-6"
        >
          <Sparkles size={12} className="text-[#C9A961]" />
          <span className="font-mono-eyebrow text-[#0A0A0A]">Ein Partner · Website · Content · Ads</span>
        </motion.div>

        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 lg:col-span-4 flex items-start gap-4">
            <span className="chapter-num mt-2">02 — Pakete</span>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <h2 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Ein Preis. Ein Team.<br />
              <em className="italic text-[#C9A961]">Ein Wachstumssystem</em>.
            </h2>
            <p className="mt-6 max-w-2xl text-[#0A0A0A]/70 text-base md:text-lg leading-relaxed">
              Statt Einzelprojekten: eine langfristige Partnerschaft, die Ihre Website weiterentwickelt, jeden Monat neuen Content produziert und Ihre Meta-Ads-Kampagnen laufend optimiert. Drei Stufen — passend zu Ihrer Grösse.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 items-stretch">
          {PARTNERS.map((p, i) => <PartnerCard key={p.id} plan={p} i={i} />)}
        </div>

        <div className="mt-12 md:mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-black/10 pt-8">
          <p className="font-serif-editorial text-2xl md:text-3xl italic max-w-xl">
            «Alle Partner-Pakete sind Festpreise — Mindestlaufzeit 6 Monate, danach monatlich kündbar.»
          </p>
          <span className="font-mono-eyebrow text-[#0A0A0A]/60">Persönliches Onboarding innerhalb 14 Tagen</span>
        </div>
      </div>
    </section>
  );
}
